import React, { useEffect, useState } from 'react';
import { Layout as AntdLayout, message, Button, Row, Col, Input, Form, Divider } from "antd";
import HeaderNoAuth from '../../components/Layout/Header/HeaderNoAuth';
import * as qs from "query-string";
import { useLocation, useHistory } from "react-router-dom";
import styled from "styled-components";
import config from 'config';
import Api from 'api';
import { markSignedIn } from '../../helper';

const LoginWrapper = styled.div`
  margin-top: 100px;
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 20px;
  text-align: center;

  h2 {
    color: #08243c;
    font-size: 28px;
    margin-bottom: 16px;
  }

  p {
    color: #666;
    font-size: 16px;
    max-width: 500px;
    margin-bottom: 32px;
    line-height: 1.6;
  }

  .csp-login-btn {
    height: 48px;
    font-size: 16px;
    padding: 0 40px;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .csp-signup-cta {
    margin-top: 20px;
    padding-top: 18px;
    border-top: 1px solid #f0f0f0;
    width: 100%;
    max-width: 380px;
    font-size: 15px;
    color: #444;

    a {
      color: #1890ff;
      font-weight: 600;
      text-decoration: underline;
    }

    .csp-signup-cta__hint {
      display: block;
      margin-top: 6px;
      font-size: 13px;
      color: #8a8787;
    }
  }

  .direct-login-form {
    width: 100%;
    max-width: 380px;
    text-align: left;
  }

  .direct-login-form .ant-form-item {
    margin-bottom: 16px;
  }

  .divider-section {
    width: 100%;
    max-width: 380px;
    margin: 24px 0;
  }
`;

interface Props {}

const LoginView = (props: Props) => {
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const queries = qs.parse(location.search);
    if (queries && queries.message) {
      message.error({ content: queries.message as string, duration: 5 });
      delete queries.message;
      history.replace({
        search: qs.stringify(queries),
      });
    }
  }, [location.search, history]);

  const handleDirectLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const { data } = await Api.post('/auth/signin', {
        email: values.email,
        password: values.password,
      });
      if (data.token) {
        markSignedIn(); // the API set the httpOnly session cookie
        history.replace('/dashboard');
      } else {
        message.error('Unexpected response. Please try again.');
      }
    } catch (e: any) {
      const errorMsg =
        e?.response?.data?.messages?.[0] ||
        'Invalid email or password.';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AntdLayout>
      <HeaderNoAuth />
      <LoginWrapper>
        <h2>Species List Pro</h2>
        <p>
          Sign in to access your species lists, field guides, and pro tools.
        </p>

        <Form
          className="direct-login-form"
          layout="vertical"
          onFinish={handleDirectLogin}
          autoComplete="on"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input
              size="large"
              placeholder="Email address"
              autoComplete="email"
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              size="large"
              placeholder="Password"
              autoComplete="current-password"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>

        <div className="divider-section">
          <Divider plain style={{ color: '#999', fontSize: 13 }}>or</Divider>
        </div>

        <Row>
          <Col>
            <Button
              size="large"
              className="csp-login-btn"
              href={config.csp_login_url}
              style={{ borderColor: '#d9d9d9' }}
            >
              Sign in via California Species Project
            </Button>
          </Col>
        </Row>
        <div className="csp-signup-cta">
          New here?{' '}
          <a href={config.csp_signup_url}>
            Create your California Species account
          </a>
          <span className="csp-signup-cta__hint">
            Species List Pro accounts are created through the California Species
            Project.
          </span>
        </div>
      </LoginWrapper>
    </AntdLayout>
  );
};

export default LoginView;
